import React, { useState } from 'react';
import './ApplicationList.css';


const ApplicationList = ({
  applications,
  loading,
  onViewDetail,
  onStatusUpdate,
  onDelete,
  currentPage,
  totalPages,
  onPageChange
}) => {
  const [statusUpdates, setStatusUpdates] = useState({});
  const [selectedCourses, setSelectedCourses] = useState({});
  const getStatusColor = (status) => {
    switch (status) {
      case 'selected':
        return '#28a745';
      case 'approoved':
        return '#fd7e14';
      case 'rejected':
        return '#dc3545';
      case 'submitted':
        return '#0d6efd';
      default:
        return '#6c757d';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatApplicantName = (application) => {
    const firstName = application.applicantFirstName || '';
    const middleName = application.applicantMiddleName || '';
    const lastName = application.applicantLastName || '';
    
    const middleInitial = middleName ? middleName.charAt(0).toUpperCase() + '.' : '';
    const fullName = `${firstName} ${middleInitial} ${lastName}`.trim();
    
    return fullName || 'N/A';
  };

  const handleStatusChange = (applicationId, newStatus) => {
    setStatusUpdates(prev => ({ ...prev, [applicationId]: newStatus }));
    
    // If status is not "selected", clear the selected course
    if (newStatus !== "selected") {
      setSelectedCourses(prev => {
        const updated = { ...prev };
        delete updated[applicationId];
        return updated;
      });
    }
  };

  const handleSelectedCourseChange = (applicationId, course) => {
    setSelectedCourses(prev => ({ ...prev, [applicationId]: course }));
  };

  const handleStatusUpdate = (applicationId) => {
    const newStatus = statusUpdates[applicationId] || applications.find(app => app.id === applicationId)?.status;
    const selectedCourse = selectedCourses[applicationId];
    
    onStatusUpdate(applicationId, newStatus, selectedCourse);
    
    // Clear the temporary state
    setStatusUpdates(prev => {
      const updated = { ...prev };
      delete updated[applicationId];
      return updated;
    });
    setSelectedCourses(prev => {
      const updated = { ...prev };
      delete updated[applicationId];
      return updated;
    });
  };

  if (loading) {
    return (
      <div className="application-list-loading">
        <div className="loading-spinner"></div>
        <p>Loading applications...</p>
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div className="application-list-empty">
        <p>No applications found.</p>
      </div>
    );
  }

  return (
    <div className="application-list">
      <div className="application-table-container">
        <table className="application-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Level</th>
              <th>Course</th>
              <th>Status</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {applications.map((application) => (
              <tr key={application.id} className="application-row">
                <td className="applicant-name">
                  {formatApplicantName(application)}
                </td>
                <td className="applicant-email">
                  {application.applicantEmail || 'N/A'}
                </td>
                <td className="level">
                  {application.level || 'N/A'}
                </td>
                <td className="course-choice">
                  {application.firstChoice || 'N/A'}--{application.firstChoiceDuration || 'N/A'}
                </td>
                <td className="status-cell">
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(application.status) }}
                  >
                    {application.status || 'Not Submitted'}
                  </span>
                </td>
                <td className="submission-date">
                  {formatDate(application.submittedAt)}
                </td>
                <td className="actions">
                  <div className="action-buttons">
                    <button
                      onClick={() => onViewDetail(application)}
                      className="btn-view"
                      title="View Details"
                    >
                      👁️ View
                    </button>
                    
                    <div className="status-update-section">
                      <select
                        value={statusUpdates[application.id] || application.status || ''}
                        onChange={(e) => handleStatusChange(application.id, e.target.value)}
                        className="status-select"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="submitted">Submitted</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="selected">Selected</option>
                      </select>
                      
                      {(statusUpdates[application.id] === "selected" || application.status === "selected") && (
                        <select
                          value={selectedCourses[application.id] || application.selectedCourse || ''}
                          onChange={(e) => handleSelectedCourseChange(application.id, e.target.value)}
                          className="course-select"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">Select Course</option>
                          {application.firstChoice && <option value={`${application.firstChoice}--${application.firstChoiceDuration}`}>{application.firstChoice}--{application.firstChoiceDuration}</option>}
                          {application.secondChoice && <option value={`${application.secondChoice}--${application.secondChoiceDuration}`}>{application.secondChoice}--{application.secondChoiceDuration}</option>}
                          {application.thirdChoice && <option value={`${application.thirdChoice}--${application.thirdChoiceDuration}`}>{application.thirdChoice}--{application.thirdChoiceDuration}</option>}
                        </select>
                      )}
                      
                      <button
                        onClick={() => handleStatusUpdate(application.id)}
                        className="btn-update"
                        title="Update Status"
                      >
                        ✅ Update
                      </button>
                    </div>
                    
                    <button
                      onClick={() => onDelete(application.id)}
                      className="btn-delete"
                      title="Delete Application"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          
          <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ApplicationList; 